import type { DataTableFilterField } from '@/components/data-table/types'
import type { ClaimHeader } from '@/features/claims/types'
import { createSchema, field } from '@/lib/store/schema'

const DECIMAL = /^([+-]?)(?:(\d+)(?:\.(\d*))?|\.(\d+))(?:[eE]([+-]?\d+))?$/
const CLAIM_DATE_FORMATTER = new Intl.DateTimeFormat('en-US', {
  year: 'numeric',
  month: 'short',
  day: 'numeric',
})
// Avoid constructing an impractically large BigInt/string when a malformed or
// adversarial decimal uses an extreme positive exponent.
const CLAIM_CURRENCY_MAX_EXPANDED_DIGITS = 1_000

export const CLAIMS_TABLE_ID = 'claims'
export const CLAIMS_DEFAULT_SORT = { id: 'service_date', desc: true }
export const CLAIMS_SORTABLE_COLUMNS = new Set([
  'source_claim_id',
  'patient_name',
  'service_date',
  'status',
  'claim_amount',
  'paid_amount',
  'provider_npi',
])

export const claimsFilterFields: DataTableFilterField<ClaimHeader>[] = [
  {
    label: 'Claim Number',
    value: 'source_claim_id',
    type: 'input',
    defaultOpen: true,
  },
  {
    label: 'Client Name',
    value: 'patient_name',
    type: 'input',
    defaultOpen: true,
  },
  {
    label: 'Service Date',
    value: 'service_date',
    type: 'timerange',
    defaultOpen: true,
  },
  {
    label: 'Status',
    value: 'status',
    type: 'input',
    defaultOpen: true,
  },
  {
    label: 'Provider NPI',
    value: 'provider_npi',
    type: 'input',
    defaultOpen: true,
  },
]

export const claimsSchema = createSchema({
  id: field.string().default(null),
  source_claim_id: field.string().default(null),
  patient_name: field.string().default(null),
  service_date: field.array(field.timestamp()).default([]),
  status: field.string().default(null),
  provider_npi: field.string().default(null),
  // oxlint-disable-next-line unicorn/no-array-sort -- schema field builder, not Array.prototype.sort
  sort: field.sort().default(CLAIMS_DEFAULT_SORT),
})

export type ClaimsFilterState = typeof claimsSchema._type

export function claimClientLabel(claim: ClaimHeader): string | null {
  return claim.patient_name?.trim() || claim.source_patient_id?.trim() || null
}

/** Parse a YYYY-MM-DD value as a local calendar date, not a UTC instant. */
export function formatClaimDate(value: string | null): string | null {
  if (!value) return null
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value)
  if (!match) return null
  const year = Number(match[1])
  const month = Number(match[2])
  const day = Number(match[3])
  const date = new Date(year, month - 1, day)
  if (
    date.getFullYear() !== year ||
    date.getMonth() !== month - 1 ||
    date.getDate() !== day
  ) {
    return null
  }
  return CLAIM_DATE_FORMATTER.format(date)
}

/** Format a decimal string as currency without losing integer precision. */
export function formatClaimCurrency(value: string | null): string | null {
  if (!value) return null
  const match = DECIMAL.exec(value)
  if (!match) return null

  const negative = match[1] === '-'
  const integer = match[2] ?? '0'
  const fraction = match[3] ?? match[4] ?? ''
  const exponentText = match[5] ?? '0'
  const exponent = Number(exponentText)
  if (!Number.isSafeInteger(exponent)) {
    return exponentText.startsWith('-') ? '$0.00' : null
  }

  const digitText = `${integer}${fraction}` || '0'
  const centsPower = exponent - fraction.length + 2
  if (centsPower > CLAIM_CURRENCY_MAX_EXPANDED_DIGITS) return null
  if (centsPower < -digitText.length) return '$0.00'

  const digits = BigInt(digitText)
  let cents: bigint
  if (centsPower >= 0) {
    cents = digits * 10n ** BigInt(centsPower)
  } else {
    const divisor = 10n ** BigInt(-centsPower)
    const quotient = digits / divisor
    const remainder = digits % divisor
    cents = quotient + (remainder * 2n >= divisor ? 1n : 0n)
  }

  const whole = cents / 100n
  const minor = (cents % 100n).toString().padStart(2, '0')
  const sign = negative && cents !== 0n ? '-' : ''
  return `${sign}$${whole.toLocaleString('en-US')}.${minor}`
}
